<template>
	<div class="forum-board-view">
		<header class="forum-board-bar" :class="{ 'forum-board-bar-search-open': isSearchOpen }">
			<!-- Search side -->
			<div class="forum-bar-search">
				<button
					v-if="!isSearchOpen"
					type="button"
					class="forum-icon-btn"
					title="Search discussions"
					aria-label="Search discussions"
					@click="openSearch"
				>
					<i class="fas fa-search"></i>
				</button>

				<div v-else class="forum-search-input-wrap">
					<i class="fas fa-search forum-search-icon"></i>
					<input
						ref="searchInput"
						type="search"
						class="forum-search-input"
						placeholder="Search discussion titles… (press Enter)"
						v-model="searchQueryDraft"
						@keydown.enter.prevent="commitSearch"
						@keydown.esc="closeSearch"
						@blur="onSearchBlur"
					/>
					<button
						type="button"
						class="forum-icon-btn forum-search-close"
						title="Close search"
						aria-label="Close search"
						@click="closeSearch"
					>
						<i class="fas fa-times"></i>
					</button>
				</div>
			</div>

			<!-- Sort tabs -->
			<div class="forum-bar-sort" v-show="!isSearchOpen">
				<button
					v-for="opt in sortOptions"
					:key="opt.value"
					type="button"
					class="forum-sort-tab"
					:class="{ 'forum-sort-tab-active': sortBy === opt.value }"
					:title="opt.label"
					:aria-label="opt.label"
					@click="selectSort(opt.value)"
				>
					<i :class="opt.icon"></i><span class="forum-sort-label">{{ opt.label }}</span>
				</button>
			</div>

			<Tooltip
				v-if="canModerateBoard && board"
				:text="board.isStudentThreadsAllowed
					? 'Students can start threads — click to disable'
					: 'Students cannot start threads — click to enable'"
			>
				<button
					type="button"
					class="forum-icon-btn forum-icon-btn-toggle"
					:class="{ 'forum-icon-btn-off': !board.isStudentThreadsAllowed }"
					:aria-label="board.isStudentThreadsAllowed
						? 'Disable student posting'
						: 'Enable student posting'"
					@click="toggleAllowStudents"
				>
					<i :class="board.isStudentThreadsAllowed ? 'fas fa-user-check' : 'fas fa-user-lock'"></i>
				</button>
			</Tooltip>

			<Tooltip
				v-if="canModerateBoard && board"
				:text="isBoardActive
					? 'Forum is on — click to disable'
					: 'Forum is disabled — click to re-enable'"
			>
				<button
					type="button"
					class="forum-icon-btn forum-icon-btn-toggle"
					:class="{ 'forum-icon-btn-off': !isBoardActive }"
					:aria-label="isBoardActive ? 'Disable forum' : 'Enable forum'"
					:disabled="isSubmittingBoardStatus"
					@click="toggleBoardStatus"
				>
					<i :class="isBoardActive ? 'fas fa-comments' : 'fas fa-comment-slash'"></i>
				</button>
			</Tooltip>

			<button
				v-if="canPostNewThread"
				type="button"
				class="forum-icon-btn forum-icon-btn-primary"
				title="Start a new discussion"
				aria-label="Start a new discussion"
				@click="openNewThread"
			>
				<i class="fas fa-plus"></i>
			</button>
		</header>

		<div v-if="shouldShowReadOnlyBanner" class="forum-readonly-banner" role="status">
			<i class="fas fa-lock"></i>
			<span class="message">This forum is disabled. Existing posts stay visible, but new threads, replies, and likes are paused until it's re-enabled.</span>
			<button
				v-if="canModerateBoard"
				type="button"
				class="reenable-btn"
				:disabled="isSubmittingBoardStatus"
				@click="enableBoardFromBanner"
			>
				{{ isSubmittingBoardStatus ? 'Enabling…' : 'Enable forum' }}
			</button>
		</div>

		<div v-if="!isSearchOpen && searchQuery" class="forum-search-active">
			<span>Filter: <strong>{{ searchQuery }}</strong></span>
			<button type="button" class="forum-link-btn" @click="clearSearch">Clear</button>
		</div>

		<div
			v-if="shouldShowNotEnabledState || !isAuthorizedForScope"
			class="forum-empty-card"
			role="status"
		>
			<div class="forum-empty-icon-circle">
				<i class="fas fa-comment-slash"></i>
			</div>
			<h3 class="forum-empty-headline">
				{{ !isAuthorizedForScope ? "You don't have access to this forum" : "Forum not enabled" }}
			</h3>
			<p class="forum-empty-body">
				<template v-if="!isAuthorizedForScope">
					This course's discussion forum is only visible to its enrolled students,
					assigned trainers, and management. If you should have access, contact your
					administrator.
				</template>
				<template v-else>
					This discussion forum hasn't been enabled yet. Contact your administrator
					or course trainer to turn it on.
				</template>
			</p>
		</div>

		<div v-else-if="showLoadingSkeleton" class="forum-thread-list">
			<article v-for="i in 5" :key="'skel-' + i" class="forum-thread-card forum-thread-skeleton">
				<header class="forum-thread-header">
					<SkeletonLine width="34px" height="34px" radius="50%" />
					<div class="forum-thread-headmeta">
						<SkeletonLine width="140px" height="12px" />
						<SkeletonLine width="90px" height="10px" margin-top="6px" />
					</div>
				</header>
				<SkeletonLine width="68%" height="14px" margin-top="10px" />
				<SkeletonLine width="100%" height="10px" margin-top="8px" />
				<SkeletonLine width="92%" height="10px" margin-top="6px" />
				<SkeletonLine width="58%" height="10px" margin-top="6px" />
			</article>
		</div>

		<!-- Empty -->
		<div v-else-if="feed.threads.length === 0" class="forum-empty-state">
			<div class="forum-empty-art">
				<i class="fas fa-comments"></i>
			</div>
			<h4>{{ searchQuery ? 'No discussions match your search' : 'No discussions yet' }}</h4>
			<p v-if="!searchQuery && canPostNewThread">Be the first to share a question, idea, or resource with this community.</p>
			<p v-else-if="!searchQuery">Posting is currently restricted on this board.</p>
			<p v-else>Try a different keyword or clear the filter.</p>
			<button v-if="!searchQuery && canPostNewThread" type="button" class="forum-btn forum-btn-primary" @click="openNewThread">
				<i class="fas fa-plus"></i> Start a discussion
			</button>
			<button v-else-if="searchQuery" type="button" class="forum-btn forum-btn-ghost" @click="clearSearch">
				Clear filter
			</button>
		</div>

		<!-- Thread list -->
		<div v-else class="forum-thread-list">
			<ThreadListItem
				v-for="thread in feed.threads"
				:key="thread.id"
				:thread="thread"
				:product-variant-id="isProductVariantScope ? entityId : null"
				:board-active="isBoardActive"
				@react="onToggleReaction"
				@pin="onTogglePin"
			/>
		</div>

		<div
			v-if="feed.nextPageToken"
			ref="threadSentinel"
			class="forum-loadmore-center"
		>
			<button
				type="button"
				class="forum-btn forum-btn-ghost"
				:disabled="feed.isLoading"
				@click="loadMore"
			>
				{{ feed.isLoading ? "Loading…" : "Load more discussions" }}
			</button>
		</div>

		<NewThreadModal
			:visible="showNewThreadModal"
			:product-variant-id="isProductVariantScope ? entityId : null"
			:is-submitting="isSubmittingNewThread"
			@close="showNewThreadModal = false"
			@submit="onSubmitNewThread"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import ThreadListItem from "./ThreadListItem.vue";
import NewThreadModal from "./NewThreadModal.vue";
import Tooltip from "../Shared/Tooltip.vue";
import SkeletonLine from "../Shared/SkeletonLine.vue";
import { UserType } from "../../constants/user";
import { BoardScope, BoardStatus } from "../../constants/forum";
import { EntityType } from "../../constants/entity";

export default {
	name: "DiscussionBoardView",
	components: { ThreadListItem, NewThreadModal, Tooltip, SkeletonLine },
	props: {
		scope: { type: String, required: true },
		entityId: { type: [Number, String], default: null },
	},
	data() {
		return {
			isSearchOpen: false,
			searchQueryDraft: "",
			searchQuery: "",
			sortBy: "recent",
			showNewThreadModal: false,
			isSubmittingNewThread: false,
			isSubmittingBoardStatus: false,
			threadObserver: null,
			sortOptions: [
				{ value: "recent",         label: "Recent",  icon: "far fa-clock" },
				{ value: "oldest",         label: "Oldest",  icon: "fas fa-history" },
				{ value: "most_reacted",   label: "Top",     icon: "fas fa-fire" },
				{ value: "most_commented", label: "Active",  icon: "far fa-comments" },
				{ value: "most_viewed",    label: "Viewed",  icon: "far fa-eye" },
			],
		};
	},
	computed: {
		...mapGetters("forum", ["feedForKey", "boardForKey"]),
		...mapState("user", ["user"]),
		feedKey() {
			return this.entityId != null ? `${this.scope}:${this.entityId}` : this.scope;
		},
		feed() {
			return this.feedForKey(this.feedKey);
		},
		board() {
			const entry = this.boardForKey(this.feedKey);
			return entry && entry.board ? entry.board : null;
		},
		isProductVariantScope() {
			return this.equalsIgnoreCase(this.scope, BoardScope.ProductVariant);
		},
		canModerateBoard() {
			if (!this.user) return false;
			if (this.equalsIgnoreCase(this.user.type, UserType.MANAGEMENT)) return true;
			if (this.equalsIgnoreCase(this.user.type, UserType.TRAINER) && this.isProductVariantScope) return true;
			return false;
		},
		isAuthorizedForScope() {
			if (!this.user) return false;
			if (!this.isProductVariantScope) return true;
			if (this.equalsIgnoreCase(this.user.type, UserType.MANAGEMENT)) return true;

			const variants = this.user.productVariants;
			if (!Array.isArray(variants)) return true;
			const requestedId = Number(this.entityId);
			if (!Number.isFinite(requestedId)) return false;
			return variants.some(
				(variant) => variant && Number(variant.id) === requestedId
			);
		},
		isBoardActive() {
			return !!(this.board && this.equalsIgnoreCase(this.board.status, BoardStatus.Active));
		},
		isBoardInactive() {
			return !!(this.board && this.equalsIgnoreCase(this.board.status, BoardStatus.Inactive));
		},
		shouldShowNotEnabledState() {
			if (this.showLoadingSkeleton) return false;
			if (!this.board) return true;
			if (this.isBoardInactive && this.feed.threads.length === 0) return true;
			return false;
		},
		shouldShowReadOnlyBanner() {
			return this.isBoardInactive && this.feed.threads.length > 0;
		},
		canPostNewThread() {
			if (!this.user) return false;
			if (!this.isBoardActive) return false;
			if (this.equalsIgnoreCase(this.user.type, UserType.MANAGEMENT)) return true;
			if (this.equalsIgnoreCase(this.user.type, UserType.TRAINER)) return true;
			return !!(this.board && this.board.isStudentThreadsAllowed);
		},
		isInitialLoad() {
			return this.feed.isLoading && this.feed.threads.length === 0;
		},
		showLoadingSkeleton() {
			return this.feed.isReloading || this.isInitialLoad;
		},
	},
	methods: {
		...mapActions("forum", [
			"fetchFeed",
			"fetchBoard",
			"addThread",
			"setThreadPinned",
			"setBoardAllowStudentThreads",
			"setBoardStatus",
		]),
		...mapActions("interactions", ["toggleReaction"]),
		equalsIgnoreCase(a, b) {
			if (a == null || b == null) return a === b;
			return String(a).toLowerCase() === String(b).toLowerCase();
		},
		showError(message, options = {}) {
			this.$toasted.error(message, { icon: "exclamation-triangle", theme: "outline", position: "bottom-center", singleton: true, duration: 3000, ...options });
		},
		showSuccess(message, options = {}) {
			this.$toasted.success(message, { icon: "check", theme: "outline", position: "bottom-center", duration: 3000, ...options });
		},
		async reloadFeed() {
			try {
				await this.fetchFeed({
					scope: this.scope,
					entityId: this.entityId,
					searchQuery: this.searchQuery || null,
					sortBy: this.sortBy,
					reload: true,
				});
			} catch (err) {
				this.showError(err?.message || "Failed to load discussions.");
			}
		},
		async loadMore() {
			if (!this.feed.nextPageToken || this.feed.isLoading) return;
			try {
				await this.fetchFeed({
					scope: this.scope,
					entityId: this.entityId,
					searchQuery: this.searchQuery || null,
					sortBy: this.sortBy,
				});
			} catch (err) {
				this.showError(err?.message || "Failed to load more.");
			}
		},
		setupThreadObserver() {
			if (this.threadObserver) {
				this.threadObserver.disconnect();
				this.threadObserver = null;
			}
			if (typeof IntersectionObserver === "undefined") return;
			this.$nextTick(() => {
				const el = this.$refs.threadSentinel;
				if (!el) return;
				this.threadObserver = new IntersectionObserver((entries) => {
					if (entries.some((entry) => entry.isIntersecting)) {
						this.loadMore();
					}
				}, { rootMargin: "240px 0px" });
				this.threadObserver.observe(el);
			});
		},
		selectSort(value) {
			if (this.sortBy === value) return;
			this.sortBy = value;
			this.reloadFeed();
		},
		openSearch() {
			this.isSearchOpen = true;
			this.searchQueryDraft = this.searchQuery;
			this.$nextTick(() => {
				if (this.$refs.searchInput) this.$refs.searchInput.focus();
			});
		},
		closeSearch() {
			this.isSearchOpen = false;
		},
		onSearchBlur() {
			if (!this.searchQueryDraft) this.closeSearch();
		},
		commitSearch() {
			const trimmed = (this.searchQueryDraft || "").trim();
			if (trimmed === this.searchQuery) return;
			this.searchQuery = trimmed;
			this.reloadFeed();
		},
		clearSearch() {
			this.searchQueryDraft = "";
			this.searchQuery = "";
			this.reloadFeed();
		},
		openNewThread() {
			this.showNewThreadModal = true;
		},
		async onSubmitNewThread({ title, body, attachmentIds }) {
			if (this.isSubmittingNewThread) return;
			this.isSubmittingNewThread = true;
			try {
				await this.addThread({
					boardScope: this.scope,
					entityId: this.entityId,
					title,
					body,
					attachmentIds: attachmentIds || [],
				});
				this.showNewThreadModal = false;
				this.showSuccess("Posted.");
				this.reloadFeed();
			} catch (err) {
				this.showError(err?.message || "Failed to post.");
			} finally {
				this.isSubmittingNewThread = false;
			}
		},
		async onToggleReaction(thread) {
			try {
				await this.toggleReaction({ entityType: EntityType.Thread, entityId: thread.id });
			} catch (err) {
				this.showError(err?.message || "Failed to toggle like.");
			}
		},
		refetchBoard() {
			this.fetchBoard({ scope: this.scope, entityId: this.entityId }).catch(() => {});
		},
		async toggleAllowStudents() {
			if (!this.board) return;
			const next = !this.board.isStudentThreadsAllowed;
			try {
				await this.setBoardAllowStudentThreads({
					boardScope: this.scope,
					entityId: this.entityId,
					isAllowed: next,
				});
				this.showSuccess(next ? "Students can now start threads." : "Students can no longer start threads.");
			} catch (err) {
				this.showError(err?.message || "Failed to update board.");
			}
		},
		async toggleBoardStatus() {
			if (!this.board) return;
			const nextActive = !this.isBoardActive;
			await this.dispatchBoardStatusChange(
				nextActive ? BoardStatus.Active : BoardStatus.Inactive,
				nextActive ? "Forum is now enabled." : "Forum disabled. Existing posts stay visible.",
			);
		},
		async enableBoardFromBanner() {
			await this.dispatchBoardStatusChange(BoardStatus.Active, "Forum is now enabled.");
		},
		async dispatchBoardStatusChange(nextStatus, successMessage) {
			if (this.isSubmittingBoardStatus) return;
			this.isSubmittingBoardStatus = true;
			try {
				await this.setBoardStatus({
					boardScope: this.scope,
					entityId: this.entityId,
					status: nextStatus,
				});
				this.showSuccess(successMessage);
				this.reloadFeed();
			} catch (err) {
				this.showError(err?.message || "Failed to update forum status.");
			} finally {
				this.isSubmittingBoardStatus = false;
			}
		},
		async onTogglePin(thread) {
			const nextPinned = !thread.isPinned;
			try {
				await this.setThreadPinned({ id: thread.id, pinned: nextPinned });
				this.showSuccess(nextPinned ? "Pinned." : "Unpinned.");
			} catch (err) {
				this.showError(err?.message || "Failed to toggle pin.");
			}
		},
	},
	watch: {
		scope() { this.reloadFeed(); this.refetchBoard(); },
		entityId() { this.reloadFeed(); this.refetchBoard(); },
		"feed.nextPageToken"() {
			this.setupThreadObserver();
		},
	},
	created() {
		this.reloadFeed();
		this.fetchBoard({ scope: this.scope, entityId: this.entityId }).catch(() => {});
	},
	mounted() {
		this.setupThreadObserver();
	},
	beforeDestroy() {
		if (this.threadObserver) this.threadObserver.disconnect();
	},
};
</script>

<style scoped>
.forum-board-view {
	width: 100%;
	margin: 0 auto;
	padding: 1.25rem 1rem 3rem;
	background: #f5f5f5;
	font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
	color: #0f0f0f;
}

/* ---------- Slim Reddit-style header bar ---------- */
.forum-board-bar {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 8px;
	margin-bottom: 14px;
	background: #ffffff;
	border: 1px solid #ececec;
	border-radius: 999px;
	transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.forum-board-bar:hover {
	border-color: #e0e0e0;
	box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.forum-bar-search {
	display: flex;
	align-items: center;
	flex: 0 0 auto;
}

.forum-board-bar-search-open .forum-bar-search {
	flex: 1 1 auto;
}

.forum-bar-sort {
	display: inline-flex;
	gap: 4px;
	flex: 1;
	justify-content: center;
}

.forum-icon-btn {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: transparent;
	border: none;
	color: #606060;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: background-color 0.15s ease, color 0.15s ease;
	font-family: inherit;
	flex-shrink: 0;
}

.forum-icon-btn:hover {
	background-color: #f2f2f2;
	color: #0f0f0f;
}

/* Toggle variants for the moderator toolbar buttons. The same circle
   shell as forum-icon-btn, but the inner icon picks up an "off" color
   so a glance distinguishes "students/forum disabled" from "active"
   without needing the text label that used to sit beside the icon. */
.forum-icon-btn-toggle i { font-size: 14px; }
.forum-icon-btn.forum-icon-btn-off i { color: #b91c1c; }
.forum-icon-btn.forum-icon-btn-off:hover { background-color: #fef2f2; }
.forum-icon-btn.forum-icon-btn-off:hover i { color: #991b1b; }
.forum-icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.forum-icon-btn:disabled:hover { background-color: transparent; }

.forum-icon-btn-primary {
	background-color: var(--secondary-color, #0f0f0f);
	color: #ffffff;
}

.forum-icon-btn-primary:hover {
	opacity: 0.88;
	color: #ffffff;
	background-color: var(--secondary-color, #0f0f0f);
}

/* Expanding search input */
.forum-search-input-wrap {
	position: relative;
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
}

.forum-search-icon {
	position: absolute;
	left: 14px;
	top: 50%;
	transform: translateY(-50%);
	color: #909090;
	font-size: 12px;
	pointer-events: none;
}

.forum-search-input {
	flex: 1;
	width: 100%;
	padding: 7px 36px 7px 34px;
	border: 1px solid #ececec;
	border-radius: 999px;
	background: #fafafa;
	font-size: 13px;
	color: #0f0f0f;
	outline: none;
	font-family: inherit;
	transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.forum-search-input:focus {
	background: #ffffff;
	border-color: #d0d0d0;
	box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.04);
}

.forum-search-close {
	position: absolute;
	right: 4px;
	top: 50%;
	transform: translateY(-50%);
	width: 28px;
	height: 28px;
}

/* Sort tabs (Reddit-style underline tabs) */
.forum-sort-tab {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	background: transparent;
	border: none;
	border-radius: 999px;
	font-size: 12.5px;
	font-weight: 500;
	color: #606060;
	cursor: pointer;
	transition: background-color 0.15s ease, color 0.15s ease;
	font-family: inherit;
}

.forum-sort-tab:hover {
	background-color: #f2f2f2;
	color: #0f0f0f;
}

.forum-sort-tab.forum-sort-tab-active {
	background-color: #f2f2f2;
	color: #0f0f0f;
	font-weight: 600;
}

.forum-sort-label {
	display: inline;
}

/* Active search-filter chip shown after the input collapses */
.forum-search-active {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 10px;
	padding: 4px 10px 4px 12px;
	background: #f2f2f2;
	border-radius: 999px;
	font-size: 12px;
	color: #606060;
}

.forum-link-btn {
	background: transparent;
	border: none;
	color: var(--secondary-color, #0f0f0f);
	font-weight: 500;
	cursor: pointer;
	padding: 2px 6px;
	border-radius: 999px;
	font-size: 12px;
	font-family: inherit;
}

.forum-link-btn:hover {
	background-color: rgba(0, 0, 0, 0.05);
}

/* ---------- Thread list ---------- */
.forum-thread-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

/* Skeleton card uses the same outer geometry as a real thread card so
   the page doesn't reflow when real data lands. */
.forum-thread-skeleton {
	background: #ffffff;
	border: 1px solid #ececec;
	border-radius: 12px;
	padding: 14px 16px;
}
.forum-thread-skeleton .forum-thread-header {
	display: flex;
	gap: 10px;
	align-items: center;
}
.forum-thread-skeleton .forum-thread-headmeta {
	flex: 1;
	min-width: 0;
}

.forum-empty-state {
	background-color: #ffffff;
	border: 1px solid #ececec;
	border-radius: 16px;
	padding: 56px 24px 48px;
	text-align: center;
}

.forum-empty-art {
	width: 72px;
	height: 72px;
	margin: 0 auto 18px;
	border-radius: 50%;
	background-color: #f2f2f2;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 28px;
	color: #606060;
}

.forum-empty-state h4 {
	font-size: 18px;
	font-weight: 600;
	color: #0f0f0f;
	margin: 0 0 8px 0;
	letter-spacing: -0.01em;
}

.forum-empty-state p {
	color: #606060;
	font-size: 14px;
	margin: 0 auto 24px;
	max-width: 380px;
	line-height: 1.5;
}

/* Buttons (kept for empty state + load-more) */
.forum-btn {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 20px;
	border-radius: 999px;
	font-size: 14px;
	font-weight: 500;
	border: none;
	cursor: pointer;
	transition: background-color 0.2s ease, color 0.2s ease,
	            border-color 0.2s ease, transform 0.1s ease;
	font-family: inherit;
}

.forum-btn:active:not(:disabled) {
	transform: scale(0.97);
}

.forum-btn-primary {
	background-color: var(--secondary-color, #0f0f0f);
	color: #ffffff;
}

.forum-btn-primary:hover:not(:disabled) {
	opacity: 0.88;
}

.forum-btn-primary:disabled {
	background-color: #f2f2f2;
	color: #909090;
	cursor: not-allowed;
}

.forum-btn-ghost {
	background-color: transparent;
	color: #0f0f0f;
	border: 1px solid #ececec;
}

.forum-btn-ghost:hover:not(:disabled) {
	background-color: #f8f8f8;
	border-color: #d0d0d0;
}

.forum-btn-ghost:disabled {
	color: #909090;
	cursor: not-allowed;
}

.forum-loadmore-center {
	display: flex;
	justify-content: center;
	padding: 22px 0 8px;
}

/* Inline empty-state card for "forum not enabled" / "not authorized"
   surfaces. Single component for both cases — copy + button visibility
   change based on which case fired. Matches the surrounding board
   surface so it doesn't feel grafted on. */
.forum-empty-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	padding: 3rem 1.5rem;
	background: #fafafa;
	border: 1px dashed #d4d4d4;
	border-radius: 12px;
	color: #404040;
	text-align: center;
}
.forum-empty-icon-circle {
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: #ffffff;
	border: 1px solid #ececec;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}
/* Explicit color on the inner <i> so a global FontAwesome stylesheet
   (or a parent-set color) can't bleed through and render the glyph
   white-on-white. */
.forum-empty-icon-circle i {
	color: #6b7280;
	font-size: 28px;
	line-height: 1;
}
.forum-empty-headline {
	margin: 0.25rem 0 0;
	font-size: 1.15rem;
	font-weight: 600;
	color: #181818;
}
.forum-empty-body {
	margin: 0;
	font-size: 1rem;
	color: #4b5563;
	max-width: 44ch;
	line-height: 1.5;
}

/* Mobile: tighten paddings + let the 4 sort tabs scroll horizontally
   if they don't fit in the narrow bar. Sort labels become icon-only on
   very narrow screens to keep everything reachable with one tap. */
@media (max-width: 600px) {
	.forum-board-view { padding: 10px 8px 32px; }
	.forum-board-bar { gap: 4px; padding: 4px 6px; }
	.forum-bar-sort {
		flex: 1 1 auto;
		justify-content: flex-start;
		overflow-x: auto;
		scrollbar-width: none;
		-webkit-overflow-scrolling: touch;
	}
	.forum-bar-sort::-webkit-scrollbar { display: none; }
	.forum-sort-tab { padding: 6px 10px; font-size: 12px; flex-shrink: 0; }
	.forum-empty-state { padding: 40px 16px 36px; }
	/* Tap targets: keep the new-post / search / toggle icon buttons at
	   36px (the minimum for thumb taps inside the bar). */
	.forum-icon-btn { width: 36px; height: 36px; }
	/* Active-search chip wraps cleanly on narrow screens. */
	.forum-search-active { flex-wrap: wrap; }
	.forum-loadmore-center { padding: 16px 0 4px; }
}
@media (max-width: 380px) {
	.forum-sort-label { display: none; }   /* icon-only on tiny phones */
	.forum-sort-tab { padding: 6px 8px; }
	/* Sort tabs collapse to icon-only — gap them tighter so all 5 fit
	   without horizontal scroll. */
	.forum-bar-sort { gap: 2px; }
}

.forum-readonly-banner {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 14px;
	background: #fff8e1;
	border: 1px solid #f3d57a;
	border-radius: 8px;
	color: #5d4a00;
	font-size: 0.875rem;
	margin-bottom: 1rem;
}
.forum-readonly-banner i { color: #b07b00; font-size: 14px; }
.forum-readonly-banner .message { flex: 1; line-height: 1.4; }
.forum-readonly-banner .reenable-btn {
	border: 1px solid #b07b00;
	background: #ffffff;
	color: #5d4a00;
	font-family: inherit;
	font-size: 13px;
	font-weight: 500;
	padding: 0.35rem 0.85rem;
	border-radius: 6px;
	cursor: pointer;
	transition: background-color 0.15s ease;
}
.forum-readonly-banner .reenable-btn:hover:not(:disabled) { background: #fff3c4; }
.forum-readonly-banner .reenable-btn:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 600px) {
	.forum-readonly-banner { flex-wrap: wrap; }
	.forum-readonly-banner .reenable-btn { width: 100%; margin-top: 4px; }
}
</style>
